'use client';

import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getCurrentUser, login as loginRequest, logout as logoutRequest } from '@/lib/api/auth';
import { clearAdminSession, getAdminSession, isAdminLogoutPending, isAdminSessionFresh, setAdminSession } from '@/lib/storage/session';
import { hasAdminAppAccess } from './access';
import { AdminAlertModal, Loader, type AdminModalVariant } from './components/common';
import { AdminLogin } from './components/AdminLogin';
import { PasswordRecovery } from './components/PasswordRecovery';

export function AdminLoginPage() {
  const router = useRouter();
  const [checkingSession, setCheckingSession] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [modal, setModal] = useState<{ message: string; type: AdminModalVariant; title?: string } | null>(null);

  useEffect(() => {
    router.prefetch('/dashboard');
    let cancelled = false;
    const checkSession = async () => {
      if (isAdminLogoutPending()) {
        clearAdminSession();
        setCheckingSession(false);
        return;
      }
      const savedSession = getAdminSession();
      if (savedSession?.user && isAdminSessionFresh() && hasAdminAppAccess(savedSession.user)) {
        router.replace('/dashboard');
        return;
      }

      try {
        const current = await getCurrentUser();
        if (cancelled) return;
        if (!hasAdminAppAccess(current.user)) {
          clearAdminSession();
          setCheckingSession(false);
          return;
        }
        setAdminSession({ user: current.user });
        router.replace('/dashboard');
        return;
      } catch {
        clearAdminSession();
      }

      if (!cancelled) setCheckingSession(false);
    };

    void checkSession();
    return () => {
      cancelled = true;
    };
  }, [router]);

  const notify = (message: string, type: AdminModalVariant = 'error', title?: string) => {
    setModal({ message, type, title });
  };

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    const loginUsername = username.trim();
    if (!loginUsername || !password) {
      notify('Enter credentials', 'warning');
      return;
    }
    setLoading(true);
    try {
      const data = await loginRequest({ username: loginUsername, password });
      if (!hasAdminAppAccess(data.user)) {
        clearAdminSession();
        await logoutRequest(data.token).catch(() => undefined);
        notify('This account does not have access to the admin application.', 'error', 'Access denied');
        return;
      }
      setAdminSession(data);
      router.replace('/dashboard');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Login failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) return <Loader message="Checking your secure session..." />;

  return (
    <>
      {recovering ? <div id="login-overlay" className="overlay" style={{display:'flex'}}><div className="auth-shell auth-shell-admin"><aside className="auth-brand-panel"><img src="/logo.png" alt="Ferty9"/><div><span className="auth-eyebrow">SECURE RECOVERY</span><h1>Return to your workspace safely.</h1><p>Identity verification protects every Ferty9 account.</p></div></aside><PasswordRecovery onBack={()=>setRecovering(false)}/></div></div> : <AdminLogin
        username={username}
        password={password}
        passwordVisible={passwordVisible}
        loading={loading}
        onUsernameChange={setUsername}
        onPasswordChange={setPassword}
        onTogglePassword={() => setPasswordVisible(value => !value)}
        onSubmit={handleSubmit}
        onForgotPassword={()=>setRecovering(true)}
      />}
      {modal && <AdminAlertModal message={modal.message} type={modal.type} title={modal.title} onClose={() => setModal(null)} />}
    </>
  );
}
